// FILE LOCATION: src/utils/assetRegistry.js

const BASE = process.env.PUBLIC_URL || '';
const LEGACY_CONFIG_URL  = `${BASE}/assets/legacy/asset-config.json`;
const CURRENT_CONFIG_URL = `${BASE}/assets/new/asset-config.json`;

// ─── Trigger key derivation ────────────────────────────────────────────────
// "Default Accordion"      → "accordion"
// "Default Tabs - 2"       → "tabs-2"
// "Default Click to reveal"→ "click-to-reveal"
export function deriveTrigger(title) {
  return title
    .toLowerCase()
    .replace(/^default\s+/i, '')
    .trim()
    .replace(/\s+-\s+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Fetch & build registry ────────────────────────────────────────────────
export async function fetchRegistry(useLegacy) {
  const configUrl = useLegacy ? LEGACY_CONFIG_URL : CURRENT_CONFIG_URL;
  const baseUrl   = configUrl.substring(0, configUrl.lastIndexOf('/') + 1);

  const configRes = await fetch(configUrl);
  if (!configRes.ok) throw new Error(`Could not reach asset-config.json (${configRes.status})`);
  const config = await configRes.json();

  if (!config.files || !Array.isArray(config.files)) {
    throw new Error('asset-config.json is missing a "files" array');
  }

  const jsonFiles = await Promise.all(
    config.files.map(async (filename) => {
      const res = await fetch(baseUrl + filename);
      if (!res.ok) throw new Error(`Could not fetch ${filename} (${res.status})`);
      return res.json();
    })
  );

  return buildRegistry(jsonFiles, useLegacy);
}

function buildRegistry(jsonFiles, isLegacy) {
  const registry = {};

  jsonFiles.forEach(jsonFile => {
    if (!jsonFile.sections) return;
    jsonFile.sections.forEach(section => {
      if (!section.items) return;
      section.items.forEach(item => {
        const trigger = deriveTrigger(item.title);
        registry[trigger] = {
          title:              item.title,
          code:               item.code || '',
          defaults:           item.defaults || {},
          legacy:             isLegacy,
          trigger,
          hasColorSelector:   item.hasColorSelector    || false,
          hasIconSelector:    item.hasIconSelector     || false,
          hasRadiusSelector:  item.hasRadiusSelector   || false,
          hasGradientSelector:item.hasGradientSelector || false,
        };
      });
    });
  });

  return registry;
}

// ─── Template assembly ─────────────────────────────────────────────────────
// Fills every %%TOKEN%% in the template string.
// Slot values come from (in priority order): overrides → item defaults → ''
export function assembleAsset(item, overrides = {}, title = '', content = '') {
  const slots = {
    ...item.defaults,
    ...overrides,
    TITLE:   title,
    CONTENT: content,
  };

  return item.code.replace(/%%([A-Z_]+)%%/g, (_, token) =>
    slots[token] !== undefined ? slots[token] : ''
  );
}

// ─── TXT file @-line parser ────────────────────────────────────────────────
// "@accordion | theme-blue-600, icon-discInfo"
// → { accordion: { COLOR_CLASS: 'theme-blue-600', ICON_CLASS: 'icon-discInfo' } }
export function parseAssetOverrides(lines) {
  const overrides = {};

  lines
    .filter(line => line.startsWith('@'))
    .forEach(line => {
      const content  = line.slice(1).trim();
      const pipeIdx  = content.indexOf('|');
      if (pipeIdx === -1) return;

      const assetKey    = content.slice(0, pipeIdx).trim().toLowerCase();
      const valueString = content.slice(pipeIdx + 1).trim();
      overrides[assetKey] = parseValueString(valueString);
    });

  return overrides;
}

// Maps class values to slot names by prefix convention
function parseValueString(valueString) {
  const slots = {};
  valueString.split(',').map(v => v.trim()).filter(Boolean).forEach(val => {
    if      (val.startsWith('theme-') || val.startsWith('gradient-')) slots['COLOR_CLASS']    = val;
    else if (val.startsWith('icon-'))                                  slots['ICON_CLASS']     = val;
    else if (val.startsWith('radius-'))                                slots['RADIUS_CLASS']   = val;
    else if (val === 'light' || val === 'dark')                        slots['LIGHT_CLASS']    = val;
  });
  return slots;
}
